import api from '.'

/** What the panel shows for a conversation: chat bubbles, and one line per tool the agent ran. */
export type AgentItem = AgentMessageItem | AgentToolItem

export type AgentMessageItem = { kind: 'message'; role: 'user' | 'assistant'; content: string }

/** A tool the agent ran. [arguments] and [output] are written for the model; show them behind a disclosure. */
export type AgentToolItem = {
	kind: 'tool'
	toolName: string
	isError: boolean
	arguments: string | null
	output: string | null
}

export type AgentPendingConfirmation = {
	tool: string
	summary: string
	arguments: Record<string, unknown>
}

/** Where the user is, sent as is. The API interprets it, so new tools never need a client change. */
export type AgentPageContext = {
	path: string
	query: Record<string, string>
}

/**
 * Something the API asks the client to do after a run. Deliberately generic: the client knows how
 * to navigate and how to reload data, nothing about individual tools. Unknown types are ignored.
 */
export type AgentAction =
	| { type: 'navigate'; path: string; query?: Record<string, string> }
	| { type: 'refresh' }
	| { type: string }

/** A conversation as the API keeps it. The panel renders it and never handles the model's history. */
export type AgentConversation = {
	conversationId: string
	title: string | null
	items: AgentItem[]
	pendingConfirmation: AgentPendingConfirmation | null
	actions?: AgentAction[]
}

/** Send either a new message or the answer to the pending confirmation. */
export type AgentRunReq = {
	conversationId: string | null
	message?: string
	approve?: boolean
	context: AgentPageContext
}

// Every use of the agent goes through art-main's /agent endpoint and picks a profile; this panel is "arty".
const ARTY_PROFILE = 'arty'

export const ArtemisAgentKeys = {
	conversation: (id: string) => ['artemis-agent', 'conversation', id],
}

const ArtemisAgentApi = {
	run: (req: AgentRunReq) =>
		api
			.post<AgentConversation>('/agent', { profile: ARTY_PROFILE, ...req }, { timeout: 120000 })
			.then((res) => res.data),

	conversation: (id: string) =>
		api.get<AgentConversation>(`/agent/conversations/${id}`).then((res) => res.data),
}

export default ArtemisAgentApi
